import React, { useEffect, useMemo, useState } from "react";

import { useParams } from "react-router-dom";

import ContentLayout from "Components/ContentLayout";
import InternalTag from "Components/InternalTag";
import PageMeta from "Components/PageMeta";
import ServiceIcon from "Components/ServiceIcon";
import { BackLink } from "ds/Button";
import { selectFeatureUsageBasedBillingEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import Conprof from "src/organization/pages/project/environment/metrics/profiling/components/Conprof/Conprof";
import Sellable from "src/organization/pages/project/environment/metrics/profiling/sellable/Sellable";
import { FlexWrapper } from "src/organization/pages/project/environment/metrics/profiling/services/style";
import { useAppSelector } from "Store/hooks";

import { NoServices } from "../components/Errors";
import useUpdateQueryParams from "../hooks/useUpdateQueryParams";

import ProfilingPageTitle from "./components/ProfilingPageTitle";
import ItemSkeleton from "./services/item/ItemSkeleton";
import ServiceList from "./services/ServiceList";
import * as S from "./style";
import { ApplicationListType, ApplicationType } from "./type";
import { useServiceList } from "./util";

/**
 * Check if the selected app and profile combination exist
 */
const selectedParamsExist = (params: any, apps?: ApplicationListType) => {
  const name = params.get("app");
  const profile = params.get("profile");
  const app = apps?.[name];
  if (app) {
    const isProfile =
      app.profile_types && Object.keys(app.profile_types).includes(profile);
    return { profile: isProfile ? profile : undefined, app };
  }
  return { profile, app: { name } };
};

const Profiling = () => {
  const params = useParams();
  const [app, setApp] = useState<ApplicationType>({
    name: params?.appName || ""
  });

  const { serviceList, error: serviceError } = useServiceList();

  useUpdateQueryParams({ app: app?.name }, false);

  const searchParams = new URLSearchParams(window.location.search);
  const paramApp = selectedParamsExist(searchParams);
  const selectedApp = app || paramApp.app;
  const appName = selectedApp?.name;

  const errors: any = serviceError;
  const hasService = serviceList.app.length && !serviceError;
  const usageBilling = useAppSelector(selectFeatureUsageBasedBillingEnabled);

  useEffect(() => {
    if (params.appName) {
      setApp({ name: params.appName });
    }
  }, [params.appName]);

  const appService = useMemo(
    () =>
      Object.values(serviceList)
        .flat()
        .find(service => service.name === selectedApp?.name),
    [selectedApp?.name, serviceList]
  );

  return appName && !errors ? (
    <ContentLayout>
      <PageMeta title="Profiling" />
      <Sellable />
      <Conprof
        customHeader={
          <>
            <BackLink
              onClick={() => {
                setApp({ name: "" });
              }}
              type=""
              from="profiling"
              to={window.location.pathname}
            />
            <FlexWrapper gap={16}>
              <ServiceIcon service={appService} alt={`${appService}-icon`} />
              <S.Heading tag="h2">
                {appName} &nbsp; {!usageBilling && <InternalTag />}
              </S.Heading>
            </FlexWrapper>
          </>
        }
        service={appName}
      />
    </ContentLayout>
  ) : (
    <div>
      <ProfilingPageTitle />
      {hasService ? (
        <ServiceList
          serviceList={serviceList}
          onViewApp={application => {
            setApp(application || { name: "" });
          }}
        />
      ) : serviceError ? (
        <NoServices />
      ) : (
        <ItemSkeleton />
      )}
    </div>
  );
};

export default Profiling;
